// YNX — useWorkspace hook
// ─────────────────────────────────────────────────────────────────────
// Encapsulates the multi-tenant workspace lifecycle:
//   • hydrates the active workspace from the local pointer (`ynx_workspace_id`)
//   • detects suspended workspaces ("blocked" by master admin)
//   • tracks whether the onboarding modal should be open and in which mode
//
// URL parameters that influence the initial state:
//   ?onboard=1  → open onboarding in 'create' mode (default)
//   ?join=1     → open onboarding in 'join' mode
//   ?billing=*  → open onboarding (return-from-Stripe)
//
// Returned shape:
//   {
//     workspace, setWorkspace,
//     workspaceLoading, workspaceBlocked,
//     showOnboarding, setShowOnboarding,
//     onboardingMode, setOnboardingMode,
//   }
import { useEffect, useState } from 'react';
import { getLocalWorkspace, getWorkspace } from '../lib/biz';

const readInitialShowOnboarding = () => {
  try {
    const p = new URLSearchParams(window.location.search);
    return p.get('onboard') === '1'
        || p.get('join') === '1'
        || p.get('billing') !== null;
  } catch (_e) {
    return false;
  }
};

const readInitialOnboardingMode = () => {
  try {
    const p = new URLSearchParams(window.location.search);
    return p.get('join') === '1' ? 'join' : 'create';
  } catch (_e) {
    return 'create';
  }
};

export const useWorkspace = () => {
  const [workspace, setWorkspace] = useState(null);
  const [workspaceLoading, setWorkspaceLoading] = useState(true);
  const [workspaceBlocked, setWorkspaceBlocked] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(readInitialShowOnboarding);
  const [onboardingMode, setOnboardingMode] = useState(readInitialOnboardingMode);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const wsId = getLocalWorkspace();
      if (!wsId) {
        if (!cancelled) setWorkspaceLoading(false);
        return;
      }
      try {
        const ws = await getWorkspace(wsId);
        if (!cancelled) setWorkspace(ws);
      } catch (err) {
        const msg = String((err && err.message) || '');
        if (msg.includes('suspendu') || msg.includes('bloqué') || msg.includes('blocked')) {
          if (!cancelled) setWorkspaceBlocked(true);
        } else {
          console.warn('YNX: workspace introuvable, on demande à recréer', err);
          try { localStorage.removeItem('ynx_workspace_id'); }
          catch (e) { console.debug('YNX: localStorage clear failed', e); }
        }
      } finally {
        if (!cancelled) setWorkspaceLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return {
    workspace, setWorkspace,
    workspaceLoading,
    workspaceBlocked,
    showOnboarding, setShowOnboarding,
    onboardingMode, setOnboardingMode,
  };
};
